import React, {useContext, useState} from 'react';

import styled from 'styled-components';
import moment from 'moment-timezone';
import { ControlLabel, FormGroup } from 'react-bootstrap';

import Modal from '../utils/ModalComponet';
import DatePicker from '../utils/FormWidgets/DatePicker';
import TimePicker from '../utils/FormWidgets/TimePicker';
import Error from '../stateMessage/Error';
import ActivitiesAutoComplete from '../utils/AutoComplete/ActivitiesAutoComplete';
import { Activity, ActivitySource, BookingOption } from '../../types/Activity';
import { FullOrderBookingOption, Order } from '../../types/Order';
import OrderBookingOptionsList from '../order/EditOrderBookingOptionList/OrderBookingOptionsList';
import ActivityBookingOptionsList from '../order/EditOrderBookingOptionList/ActivityBookingOptionsList';
import { Party } from '../../types/Party';
import {ActivityContext} from "../../contexts/Activity";

import EditPeekOrOrderForm from './EditPeekAndRedeamOrderForm';
import { isRedeamOrPeek } from './EditPeekAndRedeamOrderForm/helpers';
import { EditSingenuityOrderForm } from './EditSingenuityOrderForm';
import ActivityHiddenWarning from "./components/ActivityHiddenWarning";

const StyledDateGroup = styled(FormGroup)`
  display: flex;
  .day-picker-inp {
    margin-right: 15px;
  }
  margin-bottom: 15px;
`;

export type Fields = Pick<Order, 'startDateTime' | 'endDateTime'> & {
  timeslot?: string;
};

type Props = {
  order: Order;
  onActivitySelect(id: string): void;
  activityChangeDisabled: boolean;
  activity: Activity;
  closeModal: () => void;
  onSave(fields: Fields): void;
  orderOptions: Array<FullOrderBookingOption>;
  activityOptions: BookingOption[];
  onCancel: () => void;
  addOrderOptions(option: Partial<BookingOption>): void;
  deleteOrderOptions(option: Partial<BookingOption>): void;
  setDate(fields: Fields): void;
  attendeesChanged(id: string, attendeesCount: number): void;
  party: Party;
};

const EditOrderForm = ({
  order,
  closeModal,
  onCancel,
  onSave,
  activity,
  onActivitySelect,
  orderOptions,
  activityOptions,
  addOrderOptions,
  deleteOrderOptions,
  setDate,
  attendeesChanged,
  activityChangeDisabled,
  party,
}: Props) => {
  const {hidden:hiddenActivity} = useContext(ActivityContext);

  const { tz: partyTz = '' } = party ?? {};
  const [fields, setFields] = useState<Fields>({
    startDateTime: moment(order.startDateTime).tz(partyTz).format(),
    endDateTime: moment(order.endDateTime).tz(partyTz).format(),
  });
  const [errorText, setErrorText] = useState<string | null>(null);
  const onDateChange = (key: keyof Fields, value?: Date) => {
    const m = moment(value).tz(partyTz);
    const date = moment(fields[key]).tz(partyTz);
    const newDate = m
      .hours(date.hours())
      .minutes(date.minutes())
      .seconds(date.seconds());
    setFields({
      ...fields,
      [key]: newDate.format(),
    });
    setDate({ ...fields, [key]: m.format() });
  };

  const setValidationAppearanceTimeout = () => {
    setTimeout(() => {
      setErrorText(null);
    }, 3500);
  };

  const displayTime = (time: string | Date | undefined) => {
    const m = moment(time).tz(partyTz).format('HH:mm:ss');
    const duration = moment.duration(m);
    return Number(duration.valueOf()) / 1000;
  };

  const displayDate = (date: string | Date | undefined) =>
    moment(date).utc().tz(partyTz).toDate();

  const onTimeChange = (key: keyof Fields, value: string) => {
    const m = moment(fields[key]).tz(partyTz);
    m.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    m.add(value, 'seconds');
    setFields({
      ...fields,
      [key]: m.format(),
    });
    setDate({ ...fields, [key]: m.format() });
  };

  const onSaveOrder = () => {
    const orderStartDay = moment(fields.startDateTime);
    const orderEndDat = moment(fields.endDateTime);
    const newDate = moment(new Date());
    const dateIsBefore = newDate.isBefore(orderStartDay);
    if (orderStartDay.isAfter(orderEndDat)) {
      setErrorText('Start time is greater than end time');
      setValidationAppearanceTimeout();
      return;
    }
    if (!dateIsBefore) {
      setErrorText(
        'Сannot update the order because the purchased event has already started.'
      );
      setValidationAppearanceTimeout();
      return;
    }
    onSave(fields);
  };

  const isFareharbor = activity?.source === ActivitySource.fareharbor;

  if (isRedeamOrPeek(activity?.source)) {
    return (
      <EditPeekOrOrderForm
        onSave={onSave}
        order={order}
        closeModal={closeModal}
        party={party}
        attendeesChanged={attendeesChanged}
        orderOptions={orderOptions}
        activityOptions={activityOptions}
        addOrderOptions={addOrderOptions}
        deleteOrderOptions={deleteOrderOptions}
      />
    );
  }
  if (activity?.source === ActivitySource.singenuity) {
    return (
      <EditSingenuityOrderForm
        closeModal={closeModal}
        order={order}
        activity={activity}
        onActivitySelect={onActivitySelect}
        onDateChanged={date =>
          setDate({ startDateTime: date, endDateTime: date })
        }
        tz={partyTz}
        orderOptions={orderOptions}
        activityOptions={activityOptions}
        addOrderOptions={addOrderOptions}
        deleteOrderOptions={deleteOrderOptions}
        attendeesChanged={attendeesChanged}
        onSave={onSave}
      />
    );
  }

  return (
    <Modal
      open
      closeModal={closeModal}
      cancel={onCancel}
      action={onSaveOrder}
      actionButtonText='Save'
      classNames={{ modal: 'edit-order-form__modal' }}
    >
      {/* TODO: Fix this the next time the file is edited. */}
      {/* eslint-disable-next-line react/prop-types */}
      <h3>Edit order {order.name}</h3>
      { hiddenActivity && <ActivityHiddenWarning/> }
      {!!errorText && <Error text={errorText} />}
      <div>
        <ControlLabel>Start Date</ControlLabel>
        <StyledDateGroup>
          <DatePicker
            value={displayDate(fields.startDateTime)}
            placeholder='Start Date'
            handleChange={value => onDateChange('startDateTime', value)}
          />
          <TimePicker
            // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
            // @ts-ignore
            onChange={value => onTimeChange('startDateTime', value)}
            value={displayTime(fields.startDateTime)}
            minuteStep={15}
            style={{ alignSelf: 'center' }}
          />
        </StyledDateGroup>
        <ControlLabel>End Date</ControlLabel>
        <StyledDateGroup>
          <DatePicker
            value={displayDate(fields.endDateTime)}
            placeholder='End Date'
            handleChange={value => onDateChange('endDateTime', value)}
          />
          <TimePicker
            // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
            // @ts-ignore
            onChange={value => onTimeChange('endDateTime', value)}
            value={displayTime(fields.endDateTime)}
            minuteStep={15}
            style={{ alignSelf: 'center' }}
          />
        </StyledDateGroup>
        {!isFareharbor && (
          <>
            <FormGroup>
              <ControlLabel>Activity</ControlLabel>
              <ActivitiesAutoComplete
                disabled={activityChangeDisabled}
                initialValue={activity}
                onSelect={onActivitySelect}
                filterData={{ fareharbor: 'no' }}
              />
              {activityChangeDisabled && (
                <p className='text-danger'>
                  We do not currently support changing the experience for this
                  order. Please cancel and re-book.
                </p>
              )}
            </FormGroup>
            {/* TODO: Fix this the next time the file is edited. */}
            {/* eslint-disable-next-line react/prop-types */}
            {!!orderOptions.length && (
              <>
                <ControlLabel>Order Booking Options</ControlLabel>
                <OrderBookingOptionsList
                  options={orderOptions}
                  removeBookingOption={deleteOrderOptions}
                  onAttendeesNumberChanged={attendeesChanged}
                />
              </>
            )}
            <hr />
            {/* TODO: Fix this the next time the file is edited. */}
            {/* eslint-disable-next-line react/prop-types */}
            {!!activityOptions.length && (
              <>
                <ControlLabel>Activity Booking Options</ControlLabel>
                <ActivityBookingOptionsList
                  options={activityOptions}
                  addBookingOption={addOrderOptions}
                />
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default EditOrderForm;
